import { Temporal } from "temporal-polyfill";
import {
  LICENSE_TYPE_LABELS,
  LicenseType,
} from "@api/shared-runtime/credential-vocabularies";
import { STATES } from "@/consts/states";
import { formatDate } from "../../../lib/format-date";
import type { Credential } from "../../../types";
import type { Details } from "./detail-values";

export type CredentialSummaryInput = {
  version: NonNullable<Credential["version"]>;
  details: Details;
};

const ISO_CALENDAR_DATE_LENGTH = 10;

const SUMMARY_WORD_LABELS: Readonly<Record<string, string>> = {
  acls: "ACLS",
  bls: "BLS",
  caqh: "CAQH",
  cme: "CME",
  covid: "COVID",
  cpi: "CPI",
  cssrs: "C-SSRS",
  dea: "DEA",
  ecfmg: "ECFMG",
  esign: "Electronic signature",
  hep: "Hepatitis",
  hipaa: "HIPAA",
  id: "ID",
  mmr: "MMR",
  ndnqi: "NDNQI",
  nihss: "NIHSS",
  npi: "NPI",
  npdb: "NPDB",
  nrp: "NRP",
  pals: "PALS",
  ppe: "PPE",
  ptan: "PTAN",
  tb: "TB",
  usmle: "USMLE",
};

export function summaryText(value: unknown): string | null {
  if (typeof value === "string") {
    return value.trim() || null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

/** Stored enum spellings are labels here; names and identifiers remain verbatim. */
export function summaryLabel(value: unknown): string | null {
  const text = summaryText(value);
  if (!text) {
    return null;
  }
  const words = text.replace(/[-_]/g, " ").split(/\s+/).filter(Boolean);
  return (
    words
      .map((word) => {
        const key = word.toLowerCase();
        if (Object.hasOwn(SUMMARY_WORD_LABELS, key)) {
          return SUMMARY_WORD_LABELS[key];
        }
        return word[0].toUpperCase() + word.slice(1);
      })
      .join(" ") || null
  );
}

export function summaryFacts(values: unknown[]): string[] {
  return [
    ...new Set(values.map(summaryText).filter((value) => value !== null)),
  ];
}

export function summaryIdentifier(
  label: string,
  value: unknown,
): string | null {
  const text = summaryText(value);
  return text ? `${label} #${text}` : null;
}

export function summaryDate(value: unknown): string | null {
  const text = summaryText(value);
  if (!text) {
    return null;
  }
  try {
    return formatDate(
      Temporal.PlainDate.from(
        text.slice(0, ISO_CALENDAR_DATE_LENGTH),
      ).toString(),
    );
  } catch {
    return null;
  }
}

export function summaryDateFact(label: string, value: unknown): string | null {
  const date = summaryDate(value);
  return date ? `${label} ${date}` : null;
}

export function summaryDateRange({
  start,
  end,
  current = false,
}: {
  start: unknown;
  end: unknown;
  current?: boolean;
}): string | null {
  const from = summaryDate(start);
  const to = current ? "Present" : summaryDate(end);
  if (from && to) {
    return `${from} – ${to}`;
  }
  if (from) {
    return `From ${from}`;
  }
  if (to) {
    return current ? to : `Until ${to}`;
  }
  return null;
}

export function summaryState(value: unknown): string | null {
  const text = summaryText(value);
  if (!text) {
    return null;
  }
  return (
    STATES.find(
      ([name, code]) =>
        code === text.toUpperCase() ||
        name.toLowerCase() === text.toLowerCase(),
    )?.[0] ?? text
  );
}

export function stateCredentialTitle(state: unknown, title: string): string {
  return summaryFacts([summaryState(state), title]).join(" ");
}

export function summaryLicenseType(value: unknown): string | null {
  const text = summaryText(value);
  if (!text) {
    return null;
  }
  return (
    LICENSE_TYPE_LABELS[text.toUpperCase() as LicenseType] ?? summaryLabel(text)
  );
}

export function stateLicenseTitle({
  version,
  details,
}: CredentialSummaryInput): string {
  const state = summaryState(version.stateCode);
  if (!state && details.compact === true) {
    return "IMLC Letter of Qualification";
  }

  const discipline = summaryLicenseType(details.licenseType);
  let title = "State License";
  if (discipline === LICENSE_TYPE_LABELS[LicenseType.PHYSICIAN]) {
    title = "Medical License";
  } else if (
    discipline &&
    discipline !== LICENSE_TYPE_LABELS[LicenseType.OTHER]
  ) {
    title = `${discipline} License`;
  }
  return stateCredentialTitle(state, title);
}

export function summaryList(value: unknown): string | null {
  if (!Array.isArray(value)) {
    return null;
  }
  return summaryFacts(value).join(", ") || null;
}

/** Avoid repeating a suffix when an imported value already names the credential. */
export function summaryTitle(value: unknown, suffix: string): string {
  const label = summaryLabel(value);
  if (!label) {
    return suffix;
  }
  if (label.toLowerCase().includes(suffix.toLowerCase())) {
    return label;
  }
  return `${label} ${suffix}`;
}

export function healthScreeningTitle(value: unknown): string | null {
  const label = summaryLabel(value);
  if (!label) {
    return null;
  }
  if (/screening|test|vaccin|immuniz|titer/i.test(label)) {
    return label;
  }
  return `${label} Screening`;
}
